import { useCallback } from 'react';
import { useUpdateAtom } from 'jotai/utils';
import {
  chatFocusRequestAtom,
  chatHistoryViewAtom,
  chatOpenAtom,
  chatSessionOriginAtom,
  chatSessionResetRequestAtom,
  pendingChipAtom,
} from 'src/shared/chat';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import useAiAttention from 'src/hooks/useAiAttention';

export type PrefillOptions = {
  /**
   * Overwrite the agent's selection context with exactly these ids before the
   * draft lands. Needed by the asset-detail and post-upload surfaces, where
   * the user ticked no checkbox but the prompt still says "the selected
   * assets". Omit on the selection toolbar — the atom already holds the truth
   * and we must not disturb what the user picked.
   */
  selectAssetIds?: string[];
  /** Chip id, recorded as the session origin for attribution. */
  nudgeId?: string;
  /** Runs last — the asset modal uses it to close itself. */
  after?: () => void;
};

/**
 * Opens the AI Agent panel with a prompt pre-filled (never auto-sent).
 *
 * Ordering matters:
 *  - leave the history list first, because ChatInput is unmounted while it's
 *    showing and would never run the effect that consumes the chip;
 *  - open the panel, or the prefill lands in an invisible composer;
 *  - write a FRESH chip object every call — ChatInput's effect keys on object
 *    identity, so re-clicking the same chip must produce a new reference.
 *
 * All writes happen in one event handler, so React 17 batches them into a
 * single render and there is no open-before-chip race. ChatSidebar is always
 * mounted (AppShell gates only on config.chatEnabled), so the composer exists
 * even while the panel is closed.
 *
 * A pill that carries a prompt also starts a NEW session. Dropping a fresh
 * task into a running conversation pollutes the agent's context: it reads the
 * earlier turns as background for the new instruction and answers about the
 * wrong assets, or refuses work it already declined. The abandoned
 * conversation is saved before it is set aside, so it stays available under
 * Chat history — see `useChatSessionReset`.
 */
export function useAgentPrefill() {
  const setChatOpen = useUpdateAtom(chatOpenAtom);
  const setHistoryView = useUpdateAtom(chatHistoryViewAtom);
  const setPendingChip = useUpdateAtom(pendingChipAtom);
  const setSelectedAssets = useUpdateAtom(selectedAssetsAtom);
  const setSessionOrigin = useUpdateAtom(chatSessionOriginAtom);
  const requestNewSession = useUpdateAtom(chatSessionResetRequestAtom);
  const bumpFocus = useUpdateAtom(chatFocusRequestAtom);
  const { dismiss } = useAiAttention();

  return useCallback(
    (label: string, message: string, opts: PrefillOptions = {}) => {
      if (opts.selectAssetIds) setSelectedAssets(opts.selectAssetIds);
      setHistoryView(false);
      setChatOpen(true);
      // An empty message means "just open the panel" (the "Ask about these…"
      // affordance). Writing an empty chip would clear a draft the user had
      // already typed, so skip the write entirely.
      //
      // That case also deliberately does NOT start a new session: it carries
      // no prompt, so there is no context to pollute, and the user may well be
      // opening the panel to continue the conversation they can see.
      if (message) {
        requestNewSession((n) => n + 1);
        setPendingChip({ label, message });
      }
      // Always ask for focus: with no draft ("Ask about these…") this is the
      // only visible effect when the panel is already open, which otherwise
      // made the chip look broken.
      bumpFocus((n) => n + 1);
      // Local-only today: MessageList reads this, but the session save payload
      // doesn't carry it, so cross-session "which nudge drove this?" reporting
      // needs the origin added to the save request (chat proxy side).
      if (opts.nudgeId) setSessionOrigin(`nudge:${opts.nudgeId}`);
      // The user engaged with the agent — retire the header pulse / coach mark.
      dismiss();
      opts.after?.();
    },
    [
      setSelectedAssets,
      setHistoryView,
      setChatOpen,
      setPendingChip,
      setSessionOrigin,
      requestNewSession,
      bumpFocus,
      dismiss,
    ],
  );
}

export default useAgentPrefill;
